import zoomSdk, { Apis } from "@zoom/appssdk";

import { Client } from "@app/common/types";
import Config from "@app/config";
import logger from "@app/utils/logger";
import Tracker from "@app/utils/tracking/Tracker";

const config = {
  capabilities: [
    //APIs
    "authorize",
    "onAuthorized",
    "promptAuthorize",
    "getMeetingParticipants",
    "getRunningContext",
    "getSupportedJsApis",
    "openUrl",
    "getUserContext",
    "getMeetingJoinUrl",
    "getMeetingContext",
    "getMeetingUUID",
    "connect",
    "postMessage",
    "runRenderingContext",
    "closeRenderingContext",
    "drawParticipant",
    "clearParticipant",
    "drawImage",
    "clearImage",
    "drawWebView",
    "clearWebview",
    "sendAppInvitationToAllParticipants",
    "sendSendAppInvitation",
    "sendAppInvitationToMeetingOwner",
    "launchAppInMeeting",
    "expandApp",
    //Events
    "onConnect",
    "onMessage",
    "onMeeting",
    "onMyMediaChange",
    "onMyUserContextChange",
    "onRenderedAppOpened",
    "onSendAppInvitation",
    "onParticipantChange",
    "onActiveSpeakerChange",
    "onRunningContextChange",
  ] as Apis[],
};

// This code is from the zoom sdk that is used
// internally to detect if zoom is supported on the
// current browser
const detectBrowser = (window: any) => {
  let _a, _b, _c;
  if (window.android) {
    return {
      type: "android",
      nativeInterface: window.android,
    };
  } else if (
    (_a = window.chrome) === null || _a === void 0 ? void 0 : _a.webview
  ) {
    return {
      type: "chrome",
      nativeInterface: window.chrome.webview,
    };
  } else if (
    (_c =
      (_b = window.webkit) === null || _b === void 0
        ? void 0
        : _b.messageHandlers) === null || _c === void 0
      ? void 0
      : _c.jsOCHelper
  ) {
    return {
      type: "webkit",
      nativeInterface: window.webkit.messageHandlers.jsOCHelper,
    };
  }
};

export const isZoomSupportedPlatform = () => {
  return detectBrowser(window) != null;
};

export const isSupportedBotClient = (client: Client) => {
  return client !== "meet";
};

export const configureZoomSdk = async () => {
  if (zoomSdk != null && isZoomSupportedPlatform()) {
    logger.debug("configuring zoom sdk");
    try {
      const zoomConfig = await zoomSdk.config(config);
      Tracker.setZoomConfig(zoomConfig);
      return zoomConfig;
    } catch (error: any) {
      logger.error("error configuring zoom sdk", error);
      throw error;
    }
  } else return null;
};

// Given a url returns the friendly name for a go link, or throws
export const getGoLinkName = (url: string) => {
  const target = Object.entries(Config.goLinks).find(
    (entry) => entry[1] === url
  );

  if (!target) {
    throw Error(`no go link found for url: ${url}`);
  }

  return target[0];
};

// Returns a go link for the given url. You can almost think of this
// as a url shortener. Ex: getGoLink("https://foo.bar/fizz/buzz?stuff=things") => https://read.ai/go/buzz
// This method will raise an error if the given url is not recognized as a go link
export const getGoLink = (url: string) =>
  `${window.location.protocol}//${window.location.hostname}/go/${getGoLinkName(
    url
  )}`;
